import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import { accountGeneration, accountStorage, scopeToAccount } from '../lib/accountStorage';
import type {
  UnlockConfiguration,
  UserUnlock,
  UnlockSettings,
  FeatureCapacity,
  QuestUnlockReward,
  UnlockMode,
  FeatureType,
  UnlockType
} from '../types';
import {
  DEFAULT_UNLOCK_CONFIGURATIONS,
  CAPACITY_FORMULAS,
  calculateCapacity,
  DOMAIN_UNLOCK_ORDER
} from '../constants/unlocks';

interface UpgradesState {
  unlockConfigurations: UnlockConfiguration[];
  userUnlocks: UserUnlock[];
  unlockSettings: UnlockSettings | null;
  featureCapacities: FeatureCapacity[];
  questUnlockRewards: QuestUnlockReward[];
  // Unlock keys whose "New Unlock!" celebration has already been shown. Persisted
  // so a remount (e.g. the app's loading-spinner flicker) or a re-login never
  // re-shows a celebration the user has already seen.
  celebratedUnlockKeys: string[];
  isInitialized: boolean;
  isSyncing: boolean;
  /** Who the persisted unlock/capacity cache belongs to (anti-bleed on account switch). */
  cachedUserId: string | null;

  initialize: () => Promise<void>;
  markUnlocksCelebrated: (unlockKeys: string[]) => void;
  localDefaultConfigurations: () => UnlockConfiguration[];
  initializeStartingUnlocks: () => Promise<void>;

  isFeatureUnlocked: (featureKey: string) => boolean;
  isDomainUnlocked: (domainId: string) => boolean;
  isBreathworkStyleUnlocked: (styleId: string) => boolean;

  getFeatureCapacity: (domainId: string, featureType: FeatureType, currentLevel: number) => { current: number; max: number; unlocked: boolean };

  getUnlockedDomains: () => string[];
  getNextDomainToUnlock: () => string | null;

  unlockFeature: (unlockKey: string, questId?: string) => Promise<void>;

  setUnlockMode: (mode: UnlockMode) => Promise<void>;
  setSystemEnabled: (enabled: boolean) => Promise<void>;
  setDeveloperMode: (enabled: boolean) => Promise<void>;

  resetProgression: () => Promise<void>;
  unlockAllFeatures: () => Promise<void>;

  getUnlockStatus: (unlockKey: string) => { unlocked: boolean; canUnlock: boolean; missingRequirements: string[] };

  checkQuestUnlocks: (questId: string) => Promise<void>;
}

export const useUpgradesStore = create<UpgradesState>()(
  persist<UpgradesState, [], [], any>(
    (set, get) => ({
      unlockConfigurations: [],
      userUnlocks: [],
      unlockSettings: null,
      featureCapacities: [],
      questUnlockRewards: [],
      celebratedUnlockKeys: [],
      isInitialized: false,
      isSyncing: false,
      cachedUserId: null,

      /* ⚠️⚠️ ONE LOAD PER ACCOUNT. `isInitialized` is not saved, but a sign-out does not
         reload the page — the next account's `initialize()` returned at once and kept the
         last one's unlocks and settings (developer mode included). Every change of account
         now starts this store over (lib/accountStorage); a load still on its way for the
         account that left is dropped, and the next account's never JOINS it (the key). */
      initialize: () => dedupeInit(`unlocks:${accountGeneration()}`, async () => {
        if (get().isInitialized) return;
        const at = accountGeneration();

        try {
          const { user, error: authError } = await syncManager.safeAuthCheck();
          if (at !== accountGeneration()) return;

          if (user && !authError) {
            // Cache-first boot: the unlock/capacity state is persisted, so
            // while this user's cache is fresh, skip the five table reads and
            // just flip the session flag (still transient by design — see the
            // partialize note below).
            if (get().cachedUserId === user.id && isCloudFresh('unlocks', user.id)) {
              set({ isInitialized: true });
              // Same self-heal as a full load: a previous failed seed leaves
              // zero unlocks, so retry rather than freezing the empty state.
              if (get().userUnlocks.length === 0) {
                await get().initializeStartingUnlocks();
              }
              return;
            }

            const [configurationsResult, unlocksResult, settingsResult, capacitiesResult, rewardsResult] = await Promise.all([
              supabase.from('unlock_configurations').select('*').order('display_order'),
              supabase.from('user_unlocks').select('*').eq('user_id', user.id),
              supabase.from('unlock_settings').select('*').eq('user_id', user.id).maybeSingle(),
              supabase.from('feature_capacities').select('*').eq('user_id', user.id),
              supabase.from('quest_unlock_rewards').select('*')
            ]);
            if (at !== accountGeneration()) return;

            const configurations: UnlockConfiguration[] = (configurationsResult.data || []).map(config => ({
              id: config.id,
              unlockKey: config.unlock_key,
              unlockType: config.unlock_type as UnlockType,
              unlockName: config.unlock_name,
              unlockDescription: config.unlock_description,
              requirements: config.requirements_json,
              rewards: config.rewards_json,
              displayOrder: config.display_order,
              icon: config.icon,
              color: config.color,
              createdAt: config.created_at,
              updatedAt: config.updated_at
            }));

            const unlocks: UserUnlock[] = (unlocksResult.data || []).map(unlock => ({
              id: unlock.id,
              userId: unlock.user_id,
              unlockKey: unlock.unlock_key,
              unlockedAt: unlock.unlocked_at,
              questId: unlock.quest_id,
              unlockMode: unlock.unlock_mode as UnlockMode,
              createdAt: unlock.created_at
            }));

            const settings: UnlockSettings | null = settingsResult.data ? {
              userId: settingsResult.data.user_id,
              systemEnabled: settingsResult.data.system_enabled,
              unlockMode: settingsResult.data.unlock_mode as UnlockMode,
              developerMode: settingsResult.data.developer_mode,
              createdAt: settingsResult.data.created_at,
              updatedAt: settingsResult.data.updated_at
            } : {
              userId: user.id,
            // ⚠️⚠️ **A MISSING ROW IS A BRAND-NEW ACCOUNT, AND IT MUST NOT
            // LOCK.** `unlock_settings` has NO writer at signup — the live
            // `handle_new_user` trigger creates `public.users` and
            // `user_profiles` only, and the sole INSERT path in this codebase is
            // the three setters below, reachable only from Settings →
            // Developer. So a row exists ONLY for someone who has flipped a
            // developer toggle, and every account that has never touched that
            // screen lands here.
            //
            // This synthesized default used to say `systemEnabled: true`, which
            // made `bypassLock` FALSE in `useDisciplines` and locked all seven
            // Personal areas against a domain level of 0 (the lowest threshold,
            // Nourishment, is 1) — seven Lock rows, and an EMPTY area-tab strip
            // on the Personal domain page, because `DomainView` filters its tabs
            // on `unlocked`. It was invisible because the only accounts that
            // have ever signed in all carry a row saying `system_enabled: false`.
            // Same shape as the lockout removed on 2026-08-03: unwinnable at
            // zero, hidden from the one person who could have caught it.
            //
            // `false` matches every row that actually exists. Do not "restore"
            // this to `true` to switch the ladder on — the `unlockLevel` ladder
            // in `constants/disciplines.ts` is unreachable from level 0 and
            // would have to be made winnable first.
              systemEnabled: false,
              unlockMode: 'sequential',
              developerMode: false,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };

            const capacities: FeatureCapacity[] = (capacitiesResult.data || []).map(cap => ({
              id: cap.id,
              userId: cap.user_id,
              domainId: cap.domain_id,
              featureType: cap.feature_type as FeatureType,
              baseCapacity: cap.base_capacity,
              currentCapacity: cap.current_capacity,
              bonusCapacity: cap.bonus_capacity,
              createdAt: cap.created_at,
              updatedAt: cap.updated_at
            }));

            const rewards: QuestUnlockReward[] = (rewardsResult.data || []).map(reward => ({
              id: reward.id,
              questId: reward.quest_id,
              unlockKey: reward.unlock_key,
              rewardType: reward.reward_type,
              rewardValue: reward.reward_value,
              createdAt: reward.created_at
            }));

            // unlock_configurations is global, RLS-readable config seeded by
            // migration. Normal users cannot INSERT into it (RLS denies with
            // 403), so if it's empty we fall back to the bundled defaults
            // instead of trying to write — attempting the write here caused an
            // infinite initialize() ⇄ seed loop of failing POSTs.
            /* ⚠️⚠️ AN ACCOUNT'S FIRST LOAD ON THIS DEVICE (its saved copy is new, so no
               `cachedUserId`) has nothing celebrated — and every unlock it earned
               elsewhere would celebrate at once. They are not events here: they are the
               baseline, the same rule the v2 `migrate` below applies. */
            const firstLoadHere = get().cachedUserId === null;
            set({
              unlockConfigurations: configurations.length > 0
                ? configurations
                : get().localDefaultConfigurations(),
              userUnlocks: unlocks,
              unlockSettings: settings,
              featureCapacities: capacities,
              questUnlockRewards: rewards,
              ...(firstLoadHere
                ? { celebratedUnlockKeys: Array.from(new Set([...get().celebratedUnlockKeys, ...unlocks.map(u => u.unlockKey)])) }
                : {}),
              isInitialized: true,
              cachedUserId: user.id
            });

            if (unlocks.length === 0) {
              await get().initializeStartingUnlocks();
            }
            markCloudFresh('unlocks', user.id);
          } else {
            // Same rule as the authed no-row branch above: a signed-out /
            // local session has no settings anywhere, so it is the same
            // "brand-new account" case and must not lock either.
            const localSettings: UnlockSettings = {
              userId: 'local-user',
              systemEnabled: false,
              unlockMode: 'sequential',
              developerMode: false,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };

            set({
              unlockConfigurations: get().localDefaultConfigurations(),
              unlockSettings: localSettings,
              isInitialized: true
            });

            if (get().userUnlocks.length === 0) {
              await get().initializeStartingUnlocks();
            }
          }
        } catch (error) {
          console.error('Failed to initialize upgrades store:', error);
          if (at === accountGeneration()) set({ isInitialized: true });
        }
      }),

      markUnlocksCelebrated: (unlockKeys: string[]) => {
        if (unlockKeys.length === 0) return;
        set(state => {
          const next = new Set(state.celebratedUnlockKeys);
          let changed = false;
          unlockKeys.forEach(key => {
            if (!next.has(key)) {
              next.add(key);
              changed = true;
            }
          });
          return changed ? { celebratedUnlockKeys: Array.from(next) } : state;
        });
      },

      localDefaultConfigurations: () => {
        return DEFAULT_UNLOCK_CONFIGURATIONS.map(config => ({
          ...config,
          id: crypto.randomUUID(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        } as UnlockConfiguration));
      },

      initializeStartingUnlocks: async () => {
        await get().unlockFeature('domain_personal');
      },

      isFeatureUnlocked: (featureKey: string) => {
        const settings = get().unlockSettings;
        if (!settings?.systemEnabled || settings?.developerMode) return true;

        return get().userUnlocks.some(unlock => {
          const config = get().unlockConfigurations.find(c => c.unlockKey === unlock.unlockKey);
          return config?.rewards.featureKey === featureKey;
        });
      },

      isDomainUnlocked: (domainId: string) => {
        const settings = get().unlockSettings;
        if (!settings?.systemEnabled || settings?.developerMode) return true;

        return get().userUnlocks.some(unlock => {
          const config = get().unlockConfigurations.find(c => c.unlockKey === unlock.unlockKey);
          return config?.rewards.domainId === domainId;
        });
      },

      isBreathworkStyleUnlocked: (styleId: string) => {
        const settings = get().unlockSettings;
        if (!settings?.systemEnabled || settings?.developerMode) return true;

        return get().userUnlocks.some(unlock => {
          const config = get().unlockConfigurations.find(c => c.unlockKey === unlock.unlockKey);
          const unlockedStyles = config?.rewards.breathworkStyleId?.split(',') || [];
          return unlockedStyles.includes(styleId);
        });
      },

      getFeatureCapacity: (domainId: string, featureType: FeatureType, currentLevel: number) => {
        const settings = get().unlockSettings;
        if (!settings?.systemEnabled || settings?.developerMode) {
          return { current: 0, max: 999, unlocked: true };
        }

        const formula = CAPACITY_FORMULAS[featureType];
        const capacity = get().featureCapacities.find(
          c => c.domainId === domainId && c.featureType === featureType
        );

        const bonusCapacity = capacity?.bonusCapacity || 0;
        const maxCapacity = calculateCapacity(featureType, currentLevel, bonusCapacity);
        const unlocked = currentLevel >= formula.unlockLevel;

        return {
          current: 0,
          max: maxCapacity,
          unlocked
        };
      },

      getUnlockedDomains: () => {
        const settings = get().unlockSettings;
        if (!settings?.systemEnabled || settings?.developerMode) {
          return ['personal', 'social', 'occupational', 'avocational', 'ecological', 'intellectual', 'existential'];
        }

        const unlockedDomains: string[] = [];
        get().userUnlocks.forEach(unlock => {
          const config = get().unlockConfigurations.find(c => c.unlockKey === unlock.unlockKey);
          if (config?.rewards.domainId) {
            unlockedDomains.push(config.rewards.domainId);
          }
        });

        return unlockedDomains;
      },

      getNextDomainToUnlock: () => {
        const settings = get().unlockSettings;
        if (!settings) return null;

        const unlockedDomains = get().getUnlockedDomains();

        if (settings.unlockMode === 'sequential') {
          for (const domain of DOMAIN_UNLOCK_ORDER) {
            if (!unlockedDomains.includes(domain)) {
              return domain;
            }
          }
        }

        return null;
      },

      unlockFeature: async (unlockKey: string, questId?: string) => {
        const at = accountGeneration();
        const { user } = await syncManager.safeAuthCheck();
        // ⚠️ Asked as the account signs out — never added to (or written for) the next one.
        if (at !== accountGeneration()) return;
        const userId = user?.id || 'local-user';
        const settings = get().unlockSettings;

        const alreadyUnlocked = get().userUnlocks.some(u => u.unlockKey === unlockKey);
        if (alreadyUnlocked) return;

        const newUnlock: UserUnlock = {
          id: crypto.randomUUID(),
          userId,
          unlockKey,
          unlockedAt: new Date().toISOString(),
          questId,
          unlockMode: settings?.unlockMode,
          createdAt: new Date().toISOString()
        };

        set(state => ({
          userUnlocks: [...state.userUnlocks, newUnlock]
        }));

        if (user) {
          try {
            await supabase.from('user_unlocks').insert({
              id: newUnlock.id,
              user_id: newUnlock.userId,
              unlock_key: newUnlock.unlockKey,
              unlocked_at: newUnlock.unlockedAt,
              quest_id: newUnlock.questId,
              unlock_mode: newUnlock.unlockMode,
              created_at: newUnlock.createdAt
            });
          } catch (error) {
            console.error('Failed to sync unlock to Supabase:', error);
          }
        }
      },

      setUnlockMode: async (mode: UnlockMode) => {
        const at = accountGeneration();
        const { user } = await syncManager.safeAuthCheck();
        /* ⚠️ Asked as the account signs out: the store is the next account's by the answer —
           never set into its settings, nor upserted as its row (it asks who is signed in). */
        if (at !== accountGeneration()) return;
        const userId = user?.id || 'local-user';

        set(state => ({
          unlockSettings: state.unlockSettings ? {
            ...state.unlockSettings,
            unlockMode: mode,
            updatedAt: new Date().toISOString()
          } : null
        }));

        if (user) {
          await supabase.from('unlock_settings')
            .upsert({
              user_id: userId,
              unlock_mode: mode,
              updated_at: new Date().toISOString()
            });
        }
      },

      setSystemEnabled: async (enabled: boolean) => {
        const at = accountGeneration();
        const { user } = await syncManager.safeAuthCheck();
        if (at !== accountGeneration()) return; // asked as the account signs out — see setUnlockMode
        const userId = user?.id || 'local-user';

        set(state => ({
          unlockSettings: state.unlockSettings ? {
            ...state.unlockSettings,
            systemEnabled: enabled,
            updatedAt: new Date().toISOString()
          } : null
        }));

        if (user) {
          await supabase.from('unlock_settings')
            .upsert({
              user_id: userId,
              system_enabled: enabled,
              updated_at: new Date().toISOString()
            });
        }
      },

      setDeveloperMode: async (enabled: boolean) => {
        const at = accountGeneration();
        const { user } = await syncManager.safeAuthCheck();
        if (at !== accountGeneration()) return; // asked as the account signs out — see setUnlockMode
        const userId = user?.id || 'local-user';

        set(state => ({
          unlockSettings: state.unlockSettings ? {
            ...state.unlockSettings,
            developerMode: enabled,
            updatedAt: new Date().toISOString()
          } : null
        }));

        if (user) {
          await supabase.from('unlock_settings')
            .upsert({
              user_id: userId,
              developer_mode: enabled,
              updated_at: new Date().toISOString()
            });
        }
      },

      resetProgression: async () => {
        const at = accountGeneration();
        const { user } = await syncManager.safeAuthCheck();
        // ⚠️⚠️ Asked as the account signs out: never wipe the NEXT account's unlocks, nor delete them.
        if (at !== accountGeneration()) return;

        set({
          userUnlocks: [],
          featureCapacities: [],
          // A real reset is a fresh start — let re-unlocks celebrate again.
          celebratedUnlockKeys: []
        });

        if (user) {
          await Promise.all([
            supabase.from('user_unlocks').delete().eq('user_id', user.id),
            supabase.from('feature_capacities').delete().eq('user_id', user.id)
          ]);
        }

        await get().initializeStartingUnlocks();
      },

      unlockAllFeatures: async () => {
        const allConfigs = get().unlockConfigurations;

        for (const config of allConfigs) {
          const alreadyUnlocked = get().userUnlocks.some(u => u.unlockKey === config.unlockKey);
          if (!alreadyUnlocked) {
            await get().unlockFeature(config.unlockKey);
          }
        }
      },

      getUnlockStatus: (unlockKey: string) => {
        const unlocked = get().userUnlocks.some(u => u.unlockKey === unlockKey);
        if (unlocked) {
          return { unlocked: true, canUnlock: false, missingRequirements: [] };
        }

        const config = get().unlockConfigurations.find(c => c.unlockKey === unlockKey);
        if (!config) {
          return { unlocked: false, canUnlock: false, missingRequirements: ['Configuration not found'] };
        }

        const missingRequirements: string[] = [];

        if (config.requirements.prerequisiteUnlocks) {
          for (const prereq of config.requirements.prerequisiteUnlocks) {
            const prereqUnlocked = get().userUnlocks.some(u => u.unlockKey === prereq);
            if (!prereqUnlocked) {
              const prereqConfig = get().unlockConfigurations.find(c => c.unlockKey === prereq);
              missingRequirements.push(`Requires: ${prereqConfig?.unlockName || prereq}`);
            }
          }
        }

        const canUnlock = missingRequirements.length === 0;

        return { unlocked: false, canUnlock, missingRequirements };
      },

      checkQuestUnlocks: async (questId: string) => {
        const rewards = get().questUnlockRewards.filter(r => r.questId === questId);

        for (const reward of rewards) {
          await get().unlockFeature(reward.unlockKey, questId);
        }
      }
    }),
    {
      name: 'upgrades-storage',
      version: 2,
      /* ⚠️ One copy per account. The old shared copy names its owner (`cachedUserId`):
         only that account takes it — with its celebrated unlocks (lib/accountStorage). */
      storage: accountStorage((saved) => (saved as { cachedUserId?: string | null } | undefined)?.cachedUserId),
      // isInitialized/isSyncing are session flags. Persisting isInitialized
      // made a stale snapshot block initialize() forever (early return), so
      // configurations/settings were never refetched after a bad session.
      partialize: (state: UpgradesState) => {
        const { isInitialized, isSyncing, ...rest } = state;
        return rest;
      },
      // v1 → v2: the "celebrated" set is new. Existing users have already seen
      // their unlock celebrations (often repeatedly, which is the bug), so seed
      // the set from their current unlocks — they won't get re-celebrated.
      migrate: (persistedState: any, version: number) => {
        if (!persistedState) return persistedState;
        if (version < 2) {
          const keys = Array.isArray(persistedState.userUnlocks)
            ? persistedState.userUnlocks.map((u: any) => u?.unlockKey).filter(Boolean)
            : [];
          return { ...persistedState, celebratedUnlockKeys: Array.from(new Set(keys)) };
        }
        return persistedState;
      }
    }
  )
);

scopeToAccount(useUpgradesStore);
